import { Directive, Input, OnInit, OnDestroy } from '@angular/core';
import { EmbeddedViewRef, TemplateRef, ViewContainerRef } from '@angular/core';

import { Subscription } from 'rxjs';

import { AppState, AsyncState, UserState } from '../../../../interfaces/store.interface';
import { UserProfile, UserRoles } from '../../../../interfaces/user.interface';

import { AuthService } from '../../services/auth/auth.service';

/**
 * Compare user profile with a list of required roles
 *
 * `<ng-container *roleRequired="['Booker']">...`
 *
 * You can also specify a TemplateRef to display when the role requirements are NOT met
 *
 * ```
 *     <ng-container *roleRequired="['Booker']; else elseTemplate">...</ng-container>
 *     <ng-template #elseTemplate>Sorry, you don't have access</ng-template>
 * ```
 *
 * You can also use a 'then' template. See `*ngIf`
 *
 * NOTE: This directive will match and pass if ANY items match
 */
@Directive({
    selector: '[roleRequired]'
})
export class RoleRequiredDirective implements OnDestroy, OnInit {

    @Input() set roleRequired (roles: UserRoles | UserRoles[]) {
        // ensure value is always an array
        if (roles) {
            if (roles.constructor !== Array) {
                this.roleArray = [ <UserRoles>roles ];
            } else {
                this.roleArray = <UserRoles[]>roles;
            }
        } else {
            throw Error('[roleRequired] - roles not specified');
        }
    }

    @Input() set roleRequiredThen (templateRef: TemplateRef<any>) {
        this.thenTemplateRef = templateRef;
        this.thenViewRef = null;
        this._updateView();
    }

    @Input() set roleRequiredElse (templateRef: TemplateRef<any>) {
        this.elseTemplateRef = templateRef;
        this.elseViewRef = null;
        this._updateView();
    }

    private condition: boolean;

    private roleArray: UserRoles[];
    private subscription: Subscription;

    private thenViewRef: EmbeddedViewRef<any> = null;
    private thenTemplateRef: TemplateRef<any> = null;
    private elseViewRef: EmbeddedViewRef<any> = null;
    private elseTemplateRef: TemplateRef<any> = null;

    /**
     *
     * Define required modules for dependency injection
     *
     */
    constructor (
        templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private authService: AuthService
    ) {
        // use the contents as the default
        this.thenTemplateRef = templateRef;
    }

    ngOnInit () {

        this.subscription = this.authService.checkRoles(this.roleArray)
            .subscribe((allowed: boolean) => {
                this.condition = allowed;
                this._updateView();
            });
    }

    ngOnDestroy () {
        this.subscription.unsubscribe();
    }

    private _updateView () {
        // check condition
        if (this.condition) {
            // check if this view is already visible
            if (!this.thenViewRef) {
                // if it isn't then clear it
                this.viewContainer.clear();
                // ensure else view is not rendered
                this.elseViewRef = null;
                if (this.thenTemplateRef) {
                    // and render it again
                    this.thenViewRef = this.viewContainer.createEmbeddedView(this.thenTemplateRef);
                }
            }
        } else {
            // check if this view is already visible
            if (!this.elseViewRef) {
                // if it isn't then clear it
                this.viewContainer.clear();
                // clear the then view
                this.thenViewRef = null;
                // if an Else view has been provided render it
                if (this.elseTemplateRef) {
                    this.elseViewRef = this.viewContainer.createEmbeddedView(this.elseTemplateRef);
                }
            }
        }
    }

}
